import { useEffect, useRef, useState } from 'react';
import { cx } from '../cx';

export interface ChartPoint {
  /** Rótulo curto do eixo x (mês, data). */
  label: string;
  value: number;
  /** o rótulo por extenso, para o balão ao passar o mouse (o eixo usa `label`, curto) */
  hint?: string;
}

/**
 * Linha editorial: uma série, acento amarelo, pontos marcados, último valor destacado.
 * SVG puro, sem biblioteca. Escala do eixo y com folga de 15%. Passar o mouse (ou o dedo) mostra o rótulo e o valor do
 * ponto mais próximo; fora do gráfico, volta a valer o destaque do último.
 */
export function LineChart({ points, height = 160, unit = '', format = (v: number) => String(Math.round(v * 100) / 100), className, delta }: { points: ChartPoint[]; height?: number; unit?: string; format?: (v: number) => string; className?: string; delta?: string | null }) {
  const w = 600;
  const h = height;
  const padX = 18;
  const padT = 22;
  const padB = 26;
  const n = points.length;
  const [hover, setHover] = useState<number | null>(null);
  // a largura real do gráfico na tela: é com ela que se decide que rótulos do eixo cabem sem encostar um no outro
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [n === 0]);
  if (n === 0) return <div className={cx('rr-chart rr-chart--empty', className)} style={{ height }}>sem pontos ainda.</div>;
  const vals = points.map((p) => p.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || Math.abs(max) || 1;
  const lo = min - span * 0.15;
  const hi = max + span * 0.15;
  const x = (i: number) => (n === 1 ? w / 2 : padX + (i * (w - padX * 2)) / (n - 1));
  const y = (v: number) => padT + ((hi - v) / (hi - lo)) * (h - padT - padB);
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const area = `${d} L${x(n - 1).toFixed(1)},${(h - padB).toFixed(1)} L${x(0).toFixed(1)},${(h - padB).toFixed(1)} Z`;
  const last = points[n - 1]!;
  // o ponto mais próximo do cursor, pela posição horizontal
  const pick = (e: { clientX: number; currentTarget: Element }) => { const r = e.currentTarget.getBoundingClientRect(); if (r.width === 0 || n === 1) return setHover(0); const px = ((e.clientX - r.left) / r.width) * w; setHover(Math.max(0, Math.min(n - 1, Math.round(((px - padX) / (w - padX * 2)) * (n - 1))))); };
  const at = hover !== null && hover < n ? hover : null;
  // rótulos do eixo, em texto da página (não do SVG, que estica a letra com o gráfico): o último sempre aparece,
  // e da esquerda para a direita entra cada um que não encosta no anterior nem no último. O que não cabe fica de fora
  // (passar o mouse mostra o mês de qualquer ponto). A largura de cada rótulo é estimada pelo número de letras.
  const px = width || w, CHAR = 7.6, GAP = 14;
  const span0 = (i: number): [number, number] => { const lw = points[i]!.label.length * CHAR, c = (x(i) / w) * px, l = n === 1 ? c - lw / 2 : i === 0 ? c : i === n - 1 ? c - lw : c - lw / 2; return [l, l + lw]; };
  const ticks: number[] = [];
  if (n > 1) { const end = span0(n - 1)[0]; let prev = -Infinity; for (let i = 0; i < n - 1; i++) { const [l, r] = span0(i); if (l >= 0 && l >= prev + GAP && r + GAP <= end) { ticks.push(i); prev = r; } } }
  ticks.push(n - 1);
  return (
    <div ref={box} className={cx('rr-chart', className)} onPointerMove={pick} onPointerDown={pick} onPointerLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="rr-chart__svg" role="img" aria-label={`evolução: ${points.map((p) => `${p.label} ${format(p.value)}${unit}`).join(', ')}`}>
        <defs>
          <linearGradient id="rr-chart-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="var(--rr-accent)" stopOpacity="0.22" />
            <stop offset="1" stopColor="var(--rr-accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((t) => <line key={t} x1={padX} x2={w - padX} y1={padT + t * (h - padT - padB)} y2={padT + t * (h - padT - padB)} className="rr-chart__grid" />)}
        {n > 1 && <path d={area} fill="url(#rr-chart-fill)" />}
        <path d={d} className="rr-chart__line" />
        {at !== null && <line x1={x(at)} x2={x(at)} y1={padT - 8} y2={h - padB} className="rr-chart__guide" />}
        {points.map((p, i) => <circle key={i} cx={x(i)} cy={y(p.value)} r={i === at ? 5.5 : i === n - 1 ? 5 : 3.5} className={cx('rr-chart__dot', (at === null ? i === n - 1 : i === at) && 'rr-chart__dot--last')} />)}
      </svg>
      {ticks.map((i) => <span key={i} className={cx('rr-chart__tick', n > 1 && i === 0 && 'rr-chart__tick--start', n > 1 && i === n - 1 && 'rr-chart__tick--end')} style={{ left: `${(x(i) / w) * 100}%`, top: `${((h - padB + 7) / h) * 100}%` }}>{points[i]!.label}</span>)}
      {at === null ? (
        <div className="rr-chart__last" style={{ left: `${(x(n - 1) / w) * 100}%`, top: `${(y(last.value) / h) * 100}%` }}>
          <b>{format(last.value)}{unit}</b>
          {delta && <span>{delta}</span>}
        </div>
      ) : (
        // perto das bordas o balão fica para dentro, para não sair do gráfico
        <div className={cx('rr-chart__tip', x(at) / w < 0.12 && 'rr-chart__tip--start', x(at) / w > 0.88 && 'rr-chart__tip--end')} style={{ left: `${(x(at) / w) * 100}%`, top: `${(y(points[at]!.value) / h) * 100}%` }}>
          <span>{points[at]!.hint ?? points[at]!.label}</span>
          <b>{format(points[at]!.value)}{unit}</b>
        </div>
      )}
    </div>
  );
}

/** Barras horizontais com valor: Índice de Raridade, dimensões do O RARO. */
export function ScoreBars({ rows, max = 100 }: { rows: { label: string; value: number | null | undefined }[]; max?: number }) {
  return (
    <div className="rr-scorebars">
      {rows.map((r) => (
        <div key={r.label} className="rr-scorebar">
          <span className="rr-scorebar__label">{r.label}</span>
          <div className="rr-scorebar__track"><div className="rr-scorebar__fill" style={{ width: `${r.value == null ? 0 : Math.max(0, Math.min(100, (r.value / max) * 100))}%` }} /></div>
          <b className="rr-scorebar__value">{r.value == null ? '—' : Math.round(r.value)}</b>
        </div>
      ))}
    </div>
  );
}

/** Número grande + rótulo, como nos resultados da sala. */
export function StatBig({ value, label, tone }: { value: string; label: string; tone?: 'up' | 'down' | 'flat' }) {
  return (
    <div className="rr-stat rr-stat--tile">
      <div className={cx('rr-stat__value', tone === 'up' && 'rr-stat__value--up', tone === 'down' && 'rr-stat__value--down')}>{value}</div>
      <div className="rr-stat__label">{label}</div>
    </div>
  );
}

export interface ScatterAxis { label: string; low?: string | null; high?: string | null; min: number; max: number; format?: (v: number) => string }
export interface ScatterPoint { id: string; label: string; x: number; y: number; highlight?: boolean; /** posições anteriores, da mais antiga para a mais recente (sem a atual) */ trail?: { x: number; y: number }[] }

/**
 * Gráfico de posicionamento: marcas em dois eixos, com quadrantes e o trajeto de onde cada uma já esteve.
 * A marca em destaque (o cliente) usa o acento; as outras ficam neutras. Clicar seleciona.
 */
export function ScatterPlot({ x, y, points, selectedId, onSelect, height = 520, className }: { x: ScatterAxis; y: ScatterAxis; points: ScatterPoint[]; selectedId?: string | null; onSelect?: (id: string) => void; height?: number; className?: string }) {
  const W = 1000;
  const H = 620;
  const pad = { l: 86, r: 40, t: 34, b: 70 };
  const sx = (v: number) => pad.l + ((v - x.min) / (x.max - x.min || 1)) * (W - pad.l - pad.r);
  const sy = (v: number) => H - pad.b - ((v - y.min) / (y.max - y.min || 1)) * (H - pad.t - pad.b);
  const fx = x.format ?? ((v: number) => String(Math.round(v)));
  const fy = y.format ?? ((v: number) => String(Math.round(v)));
  const ticks = (a: ScatterAxis) => [0, 0.25, 0.5, 0.75, 1].map((t) => a.min + t * (a.max - a.min));
  if (points.length === 0) return <div className={cx('rr-chart rr-chart--empty', className)} style={{ height }}>nenhuma marca com dado nos dois eixos ainda.</div>;
  return (
    <div className={cx('rr-scatter', className)} style={{ height }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="rr-scatter__svg" role="img" aria-label={`posicionamento: ${x.label} por ${y.label}`}>
        {ticks(x).map((v, i) => <g key={`x${i}`}><line className={cx('rr-scatter__grid', i === 2 && 'rr-scatter__grid--mid')} x1={sx(v)} x2={sx(v)} y1={pad.t} y2={H - pad.b} /><text className="rr-scatter__tick" x={sx(v)} y={H - pad.b + 20} textAnchor="middle">{fx(v)}</text></g>)}
        {ticks(y).map((v, i) => <g key={`y${i}`}><line className={cx('rr-scatter__grid', i === 2 && 'rr-scatter__grid--mid')} x1={pad.l} x2={W - pad.r} y1={sy(v)} y2={sy(v)} /><text className="rr-scatter__tick" x={pad.l - 10} y={sy(v) + 4} textAnchor="end">{fy(v)}</text></g>)}
        <text className="rr-scatter__axis" x={(pad.l + W - pad.r) / 2} y={H - 14} textAnchor="middle">{x.label}</text>
        {x.low && <text className="rr-scatter__end" x={pad.l} y={H - 14} textAnchor="start">← {x.low}</text>}
        {x.high && <text className="rr-scatter__end" x={W - pad.r} y={H - 14} textAnchor="end">{x.high} →</text>}
        <text className="rr-scatter__axis" transform={`translate(18 ${(pad.t + H - pad.b) / 2}) rotate(-90)`} textAnchor="middle">{y.label}</text>
        {y.high && <text className="rr-scatter__end" transform={`translate(18 ${pad.t}) rotate(-90)`} textAnchor="end">{y.high} →</text>}
        {y.low && <text className="rr-scatter__end" transform={`translate(18 ${H - pad.b}) rotate(-90)`} textAnchor="start">← {y.low}</text>}
        {points.map((p) => {
          const on = selectedId === p.id;
          const trail = [...(p.trail ?? []), { x: p.x, y: p.y }];
          return (
            <g key={p.id} className={cx('rr-scatter__brand', p.highlight && 'rr-scatter__brand--client', on && 'rr-scatter__brand--on', selectedId && !on && 'rr-scatter__brand--dim')} onClick={() => onSelect?.(p.id)} role={onSelect ? 'button' : undefined}>
              {trail.length > 1 && <polyline className="rr-scatter__trail" points={trail.map((t) => `${sx(t.x)},${sy(t.y)}`).join(' ')} />}
              {(p.trail ?? []).map((t, i) => <circle key={i} className="rr-scatter__ghost" cx={sx(t.x)} cy={sy(t.y)} r={4} />)}
              <circle className="rr-scatter__dot" cx={sx(p.x)} cy={sy(p.y)} r={p.highlight ? 11 : 8} />
              <text className="rr-scatter__label" x={sx(p.x) + (sx(p.x) > W - 180 ? -16 : 16)} y={sy(p.y) + 5} textAnchor={sx(p.x) > W - 180 ? 'end' : 'start'}>{p.label}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
